import { api } from '..';
import { CredentialFiltersValueStateType } from '../../stores';

import {
  fetchCredentialsFiltersUrl,
  fetchCredentialsListUrl,
  fetchCredentialsCreateUrl,
  fetchCredentialsUploadUrl,
  fetchCredentialsUploadDocumentUrl,
  fetchCredentialsDownloadDocumentUrl,
  fetchCredentialsViewUrl,
  fetchCredentialsRenewUrl,
  fetchCredentialsExportUrl,
  fetchCredentialsDeleteUrl,
  fetchCredentialsCPFUrl,
} from './constants';

import {
  CredentialsFiltersResponseType,
  CredentialsListResponseType,
  CredentialsCreateResponseType,
  CredentialsUpdateResponseType,
  CredentialsUploadDocumentResponseType,
  CredentialsViewResponseType,
  CredentialsRenewResponseType,
  CredentialsExportResponseType,
  CredentialsDeleteResponseType,
  CredentialsCPFResponseType,
} from './types';

export const fetchCredentialsFiltersService = async () => {
  const response = await api.get<CredentialsFiltersResponseType>(
    fetchCredentialsFiltersUrl,
  );
  return response.data;
};

export const fetchCredentialsListService = async (params: {
  number?: string;
  licensePlate?: string;
  holderName?: string;
  holderDocument?: string;
  street?: string;
  routeId?: string;
  expirationDate?: string;
  status?: '' | 1 | 2;
  page?: string;
}) => {
  const response = await api.get<CredentialsListResponseType>(
    fetchCredentialsListUrl,
    { params },
  );
  return response.data;
};

export const fetchCredentialsCreateService = async (params: {
  expirationDate: string;
  restricted: number;
  licensePlate: string;
  vehicleBrand: string;
  vehicleColor: string;
  holderName: string;
  holderDocument: string;
  holderPhone: string;
  documents: string;
  postalCode: string;
  street: string;
  streetNumber: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  observation: string;
  routes: number[];
}) => {
  const response = await api.post<CredentialsCreateResponseType>(
    fetchCredentialsCreateUrl,
    params,
  );
  return response.data;
};

export const fetchCredentialsUpdateService = async (params: {
  id: number;
  expirationDate: string;
  restricted: number;
  licensePlate: string;
  vehicleBrand: string;
  vehicleColor: string;
  holderName: string;
  holderDocument: string;
  holderPhone: string;
  documents: string;
  postalCode: string;
  street: string;
  streetNumber: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  observation: string;
  routes: number[];
}) => {
  const response = await api.put<CredentialsUpdateResponseType>(
    `${fetchCredentialsUploadUrl}/${params.id}`,
    params,
  );
  return response.data;
};

export const fetchCredentialsUploadDocumentService = async (
  params: FormData | undefined,
) => {
  const response = await api.post<CredentialsUploadDocumentResponseType>(
    fetchCredentialsUploadDocumentUrl,
    params,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const fetchCredentialsDownloadDocumentService = async (params: {
  fileName: string;
}) => {
  const response = await api.get(
    `${fetchCredentialsDownloadDocumentUrl}?fileName=${params.fileName}`,
    {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};

export const fetchCredentialsViewService = async (id: number) => {
  const response = await api.get<CredentialsViewResponseType>(
    `${fetchCredentialsViewUrl}/${id}`,
  );
  return response.data;
};

export const fetchCredentialsRenewService = async (params: {
  id: number;
  expirationDate: string;
}) => {
  const { id, expirationDate } = params;

  const response = await api.put<CredentialsRenewResponseType>(
    `/credentials/${id}${fetchCredentialsRenewUrl}`,
    { expirationDate },
  );
  return response.data;
};

export const fetchCredentialsExportService = async (params: {
  number: number;
  licensePlate: string;
  holderName: string;
  holderDocument: string;
  street: string;
  routeId: string;
  expirationDate: string;
}) => {
  const {
    number,
    licensePlate,
    holderName,
    holderDocument,
    street,
    routeId,
    expirationDate,
  } = params;

  const response = await api.get<CredentialsExportResponseType[]>(
    `${fetchCredentialsExportUrl}?number=${number}&licensePlate=${licensePlate}&holderName=${holderName}&holderDocument=${holderDocument}&street=${street}&routeId=${routeId}&expirationDate=${expirationDate}`,
  );
  return response.data;
};

export const fetchCredentialsExportExcelService = async (
  params: CredentialFiltersValueStateType,
) => {
  const {
    number,
    licensePlate,
    holderName,
    holderDocument,
    street,
    routeId,
    expirationDate,
  } = params;

  const response = await api.get<CredentialsExportResponseType[]>(
    `${fetchCredentialsExportUrl}?number=${number}&licensePlate=${licensePlate}&holderName=${holderName}&holderDocument=${holderDocument}&street=${street}&routeId=${routeId}&expirationDate=${expirationDate}`,
  );

  return response.data;
};

export const fetchCredentialsExportHtmlService = async (
  params: CredentialFiltersValueStateType,
) => {
  const {
    number,
    licensePlate,
    holderName,
    holderDocument,
    street,
    routeId,
    expirationDate,
  } = params;

  const response = await api.get<CredentialsExportResponseType[]>(
    `${fetchCredentialsExportUrl}?number=${number}&licensePlate=${licensePlate}&holderName=${holderName}&holderDocument=${holderDocument}&street=${street}&routeId=${routeId}&expirationDate=${expirationDate}`,
  );

  return response.data;
};

export const fetchCredentialsDeleteService = async (params: { id: number }) => {
  const response = await api.delete<CredentialsDeleteResponseType>(
    `${fetchCredentialsDeleteUrl}/${params.id}`,
  );
  return response.data;
};

export const fetchCredentialsCPFService = async (params: { cpf: string }) => {
  const response = await api.get<CredentialsCPFResponseType>(
    `${fetchCredentialsCPFUrl}/${params.cpf}`,
  );

  return response.data;
};
